import { ProductCarousel } from 'components/Carousel';
import Img from 'components/Img';
import { LayoutContainer } from 'components/Layout';
import { ScButton, type ValidSitecoreLink } from 'components/SitecoreLink';
import Text from 'components/Text';
import ThemedBlockContainer from 'components/ThemedBlockContainer';
import type { GTMItemListId, GTMItemListName } from 'hooks/gtm';
import type { ProductCard as ProductCardType } from 'models/productCard';
import type { Field, HeadingLevel } from 'models/sitecore';
import type { ColorString } from 'types';

interface Props {
	centerImageAlt: string;
	centerImageLargeSrc: string | undefined;
	centerImageSmallSrc: string | undefined;
	className?: string;
	color: ColorString;
	gtmItemListId: GTMItemListId;
	gtmItemListName: GTMItemListName;
	heading?: Field<string>;
	headingLevel?: HeadingLevel;
	id?: string;
	link?: ValidSitecoreLink;
	links: ValidSitecoreLink[];
	linksHeading: string | undefined;
	products: ProductCardType[];
	text?: Field<string>;
	topLeftImageSrc: string | undefined;
}

export default function ThemedProductCarousel({
	centerImageAlt,
	centerImageLargeSrc,
	centerImageSmallSrc,
	className,
	color,
	gtmItemListId,
	gtmItemListName,
	heading,
	headingLevel = 'h2',
	id,
	link,
	links,
	linksHeading,
	products,
	text,
	topLeftImageSrc,
}: Props) {
	return (
		<LayoutContainer id={id} outerClassName={className}>
			<Text
				as={headingLevel}
				field={heading}
				className={text ? 'mb-2' : 'mb-4'}
			/>
			<Text as="p" field={text} className="mb-6" />

			<ThemedBlockContainer
				color={color}
				topLeftImageSrc={topLeftImageSrc}
				className="max-md:-mx-4"
				contentClassName="py-4 md:grid md:grid-cols-12 md:items-center md:gap-x-6 md:py-12"
			>
				<div className="relative self-stretch text-center max-md:mb-6 md:col-span-4 md:flex md:items-center">
					{centerImageSmallSrc && centerImageLargeSrc && (
						<>
							<Img
								src={centerImageSmallSrc}
								alt={centerImageAlt}
								width={360}
								height={180}
								jpgOptimized={false}
								className="mx-auto md:hidden"
							/>
							<Img
								src={centerImageLargeSrc}
								alt={centerImageAlt}
								width={532}
								height={532}
								jpgOptimized={false}
								className="mx-auto max-md:hidden"
							/>
						</>
					)}
					{link && (
						<ScButton
							link={link}
							variant="secondary"
							className="mt-2 focus-visible:focus-outline-inverted md:absolute md:bottom-0 md:left-1/2 md:-translate-x-1/2"
						>
							{link.text}
						</ScButton>
					)}
				</div>

				<div className="rounded-l-md bg-base pt-3 pb-4 text-base max-md:ml-4 md:col-span-8 md:self-stretch md:pb-6">
					<ProductCarousel
						products={products}
						sizing="themed-block"
						gtmItemListId={gtmItemListId}
						gtmItemListName={gtmItemListName}
						microContentLinks={links}
						microContentLinksTitle={linksHeading}
						gutter="md"
						pullGutter={false}
					/>
				</div>
			</ThemedBlockContainer>
		</LayoutContainer>
	);
}
ThemedProductCarousel.displayName = 'ThemedProductCarousel';
